import * as THREE from 'three';
import { TrainingTrackElement, TrainingTrackElementType } from '../../../../../shared/src/trainingTrack.types';

/**
 * Base class for all training track elements
 */
export abstract class TrackElement {
    protected object: THREE.Object3D | null = null;
    protected id: string;
    protected type: TrainingTrackElementType;
    protected properties: any;
    
    /**
     * Constructor
     * @param element The element data
     */
    constructor(protected element: TrainingTrackElement) {
        this.id = element.id;
        this.type = element.type;
        this.properties = element.properties || {};
    }
    
    /**
     * Creates the visual representation of the element
     * @param scene The Three.js scene
     */
    public create(scene: THREE.Scene): void {
        this.object = this.createObject();
        
        if (this.object) {
            // Store element data in object's userData
            this.object.userData.elementId = this.id;
            this.object.userData.elementType = this.type;
            this.object.userData.properties = this.properties;
            
            // Position and rotate the object
            this.object.position.copy(this.element.position);
            this.object.rotation.copy(this.element.rotation);
            
            // Add to scene
            scene.add(this.object);
        }
    }
    
    /**
     * Creates the Three.js object for this element
     * @returns The created object
     */
    protected abstract createObject(): THREE.Object3D;
    
    /**
     * Updates the element
     * @param deltaTime Time elapsed since last update in seconds
     */
    public update(deltaTime: number): void {
        // Base implementation does nothing
    }
    
    /**
     * Handles player interaction with this element
     * @param playerPosition The player's position
     * @param playerTank The player's tank
     * @param distance The distance to the player
     * @returns True if interaction was handled, false otherwise
     */
    public handleInteraction(playerPosition: THREE.Vector3, playerTank: any, distance: number): boolean {
        // Base implementation does nothing
        return false;
    }
    
    /**
     * Gets the Three.js object for this element
     * @returns The object or null if not created
     */
    public getObject(): THREE.Object3D | null {
        return this.object;
    }
    
    /**
     * Gets the element ID
     * @returns The element ID
     */
    public getId(): string {
        return this.id;
    }
    
    /**
     * Gets the element type
     * @returns The element type
     */
    public getType(): TrainingTrackElementType {
        return this.type;
    }
    
    /**
     * Gets the element properties
     * @returns The element properties
     */
    public getProperties(): any {
        return this.properties;
    }
    
    /**
     * Removes the element from the scene
     * @param scene The Three.js scene
     */
    public remove(scene: THREE.Scene): void {
        if (this.object) {
            scene.remove(this.object);
            this.object = null;
        }
    }
}